import React, { useMemo } from 'react';
import { CheckboxDropdown } from '@/components/uiKit/Dropdown/CheckboxDropdown';
import { useOperatorProficiencyFilters } from '@/hooks/AssetsPreviewPage/OperatorProficiency/useOperatorProficiencyFilters';

export const AssetTypeDropdown: React.FC = () => {
  const { assetTypeFilter, handleMultiSelect } = useOperatorProficiencyFilters();

  // Transform filter options to CheckboxDropdown format
  const groups = useMemo(() => {
    if (!assetTypeFilter) return [{ options: [] }];

    return [
      {
        options: assetTypeFilter.options.map((opt) => ({
          id: opt.id,
          label: opt.label,
          checked: opt.checked,
        })),
      },
    ];
  }, [assetTypeFilter]);

  // Compute trigger text based on selection state
  const triggerText = useMemo(() => {
    if (!assetTypeFilter) return 'Asset Types';

    const checkedOptions = assetTypeFilter.options.filter((opt) => opt.checked);
    const totalOptions = assetTypeFilter.options.length;

    // All selected or none selected → show default label
    if (checkedOptions.length === 0 || checkedOptions.length === totalOptions) {
      return 'Asset Types';
    }

    // Single selection → show the selected item label
    if (checkedOptions.length === 1) {
      return checkedOptions[0].label;
    }

    // Multiple but not all → show first item + count badge
    const firstLabel = checkedOptions[0].label;
    const remainingCount = checkedOptions.length - 1;

    return (
      <span className="flex items-center gap-1.5">
        <span className="truncate max-w-[120px]">{firstLabel}</span>
        <span className="inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded">
          +{remainingCount}
        </span>
      </span>
    );
  }, [assetTypeFilter]);

  const handleChange = (optionId: string, checked: boolean) => {
    handleMultiSelect('asset_type', optionId, checked);
  };

  return (
    <CheckboxDropdown
      triggerText={triggerText}
      triggerVariant="outline"
      triggerSize="sm"
      groups={groups}
      onChange={handleChange}
      requireSelection={true}
    />
  );
};
